import type { ComboboxItem, OptionsFilter } from "@mantine/core";
import { type CountryCode, getCountries, getCountryCallingCode } from "libphonenumber-js";

/**
 * List of all countries formatted as combobox items.
 * Generated from libphonenumber-js country list.
 */
export const ALL_COUNTRY_ITEMS: ComboboxItem[] = getCountries().map((country: CountryCode) => ({
    value: country,
    label: country,
}));

/**
 * Filters country codes based on an allowlist.
 *
 * @param allowedCountryCodes - Optional list of ISO2 country codes to include (case-insensitive)
 * @param allCountryCodes - Full list of country codes to filter from. Defaults to all countries
 * @returns Filtered list of allowed country codes
 *
 * @example
 * const filtered = getFilteredCountryCodes(["US", "CA"]);
 * // Returns: ["US", "CA"]
 *
 * @example
 * const all = getFilteredCountryCodes();
 * // Returns: all available country codes
 */
export function getFilteredCountryCodes(allowedCountryCodes?: CountryCode[]): CountryCode[] {
    const allCountryCodes: CountryCode[] = getCountries();
    if (!allowedCountryCodes?.length) return allCountryCodes;

    // Normalize to match libphonenumber-js CountryCode format (uppercase) e.g "US"
    const normalizedAllowed = new Set(allowedCountryCodes.map((code) => code?.toUpperCase() as CountryCode));

    const allowedSet = new Set(normalizedAllowed);
    return allCountryCodes.filter((code) => allowedSet.has(code));
}

/**
 * Gets items for countries based on an allowlist.
 *
 * @param enabledCountryCodes - Optional list of ISO2 country codes to enable.
 * @returns Filtered list of country items.
 */
export function getCountryItems(enabledCountryCodes?: CountryCode[]): ComboboxItem[] {
    const codes = getFilteredCountryCodes(enabledCountryCodes);
    return codes.map((code) => ({ value: code, label: code }));
}

/**
 * Prepares a country name for comparison.
 * Removes leading '+' if present.
 *
 * @param value - String assumed to be a country name.
 * @returns - Lowercase string without whitespace.
 */
export const normalizeCountryQuery = (value: string) => {
    return value.toLowerCase().trim().replace(/^\+/, ""); // Remove leading '+' for dial code matching
};

/**
 * Filters items based on name, country code, or dial code - e.g "United States" or US or +1.
 *
 * @param options - List of country items.
 * @param search - Search query.
 * @returns Filtered list of country items.
 */
export const countryNameFilter: OptionsFilter = ({ options, search }) => {
    const query = normalizeCountryQuery(search);
    const displayNames = new Intl.DisplayNames(["en"], { type: "region", fallback: "none" });

    return (options as ComboboxItem[]).filter((item) => {
        const code = item.value as CountryCode;
        const name = displayNames.of(code) ?? code;
        const dialCode = getCountryCallingCode(code);

        return (
            normalizeCountryQuery(name).includes(query) ||
            normalizeCountryQuery(code).includes(query) ||
            normalizeCountryQuery(dialCode).includes(query)
        );
    });
};

/**
 * Intl API instance to get country names.
 *
 * - `fallback: "none"` means it returns `undefined` if there's no matching display name.
 * - `type: "region"` indicates DisplayNames returns a name.
 *
 * @param locale - Defaults to `["en"]` if browser languages are not available.
 * @returns Localized list of Intl.DisplayNames.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DisplayNames/DisplayNames#fallback
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/languages
 */
const getRegionNames = (locale: string[] = ["en"]): Intl.DisplayNames => {
    return new Intl.DisplayNames(navigator.languages ?? locale, { type: "region", fallback: "none" });
};

/**
 * @param value - String that is assumed to be a CountryCode e.g "US"
 * @returns Localized region name or fallback.
 */
export const getRegionName = (code: CountryCode): string => {
    return getRegionNames().of(code) ?? code;
};
